import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Result, Skeleton, Space, Tag, Typography } from "antd";
import {
  ArrowLeftOutlined,
  CopyOutlined,
  DownloadOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { errorText, getInvoice, openPortalFile } from "./api";
import { formatDay, formatEuro } from "../../../serviceProvider/components/workReports/format";
import { paymentState } from "./labels";
import "./portal.css";

const { Paragraph, Text, Title } = Typography;

/** "DE89370400440532013000" → "DE89 3704 0044 0532 0130 00" */
const groupIban = iban =>
  String(iban || "")
    .replace(/(.{4})/g, "$1 ")
    .trim();

/** One invoice: amounts, payment state, the transfer data with GiroCode, and the PDF. */
const PortalInvoiceDetail = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const [state, setState] = useState({ loading: true });
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    getInvoice(id)
      .then(res => setState({ loading: false, invoice: res.data.data }))
      .catch(err => setState({ loading: false, failed: errorText(err, t("Invoice not found")) }));
  }, [id, t]);

  const pdf = async download => {
    setError("");
    try {
      await openPortalFile(
        `/invoices/${id}/pdf${download ? "?download=1" : ""}`,
        `${state.invoice.number || "Rechnung"}.pdf`,
        { download }
      );
    } catch (err) {
      setError(errorText(err, t("The PDF could not be created.")));
    }
  };

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      setCopied("");
    }
  };

  if (state.loading) {
    return (
      <div className="portal-page">
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }
  if (state.failed) {
    return (
      <div className="portal-page">
        <Result
          status="404"
          title={state.failed}
          extra={<Link to="/client/invoices">{t("Back to the invoices")}</Link>}
        />
      </div>
    );
  }

  const inv = state.invoice;
  const tag = paymentState(t, inv.paymentState);
  const pay = inv.payment;

  return (
    <div className="portal-page">
      <Link className="portal-back" to="/client/invoices">
        <ArrowLeftOutlined aria-hidden="true" /> {t("Invoices")}
      </Link>
      {error && <Alert className="portal-gap" type="error" showIcon message={error} />}
      <Card className="portal-card portal-gap">
        <div className="portal-doc-head">
          <div>
            <Title level={4}>{inv.title || inv.number}</Title>
            <Text type="secondary">
              {[inv.number, formatDay(inv.issueDate)].filter(Boolean).join(" · ")}
            </Text>
          </div>
          <Space wrap>
            <Tag color={tag.color}>{tag.text}</Tag>
            <Button icon={<FilePdfOutlined />} onClick={() => pdf(false)}>
              PDF
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => pdf(true)}
              aria-label={t("Download")}
            />
          </Space>
        </div>
        <Descriptions size="small" column={1} className="portal-gap-top">
          {inv.project && (
            <Descriptions.Item label={t("Project")}>{inv.project.name}</Descriptions.Item>
          )}
          {inv.dueDate && (
            <Descriptions.Item label={t("Due")}>{formatDay(inv.dueDate)}</Descriptions.Item>
          )}
          {inv.grossCents !== null && (
            <Descriptions.Item label={t("Amount")}>
              {formatEuro(inv.grossCents, lng)}
            </Descriptions.Item>
          )}
          {inv.paidCents > 0 && (
            <Descriptions.Item label={t("Paid")}>
              {formatEuro(inv.paidCents, lng)}
            </Descriptions.Item>
          )}
          {inv.openCents !== null && (
            <Descriptions.Item label={t("Open")}>
              <strong>{formatEuro(inv.openCents, lng)}</strong>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {pay?.directDebit && (
        <Alert
          type="info"
          showIcon
          message={t(
            "This invoice is collected by SEPA direct debit. You do not need to transfer anything."
          )}
        />
      )}
      {pay && !pay.directDebit && (
        <Card title={t("Pay by bank transfer")} className="portal-card">
          <div className="portal-pay">
            {pay.girocode && (
              <figure className="portal-girocode">
                <img
                  src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(pay.girocode.svg)}`}
                  alt={t("GiroCode for your banking app")}
                />
                <figcaption>{t("Scan with your banking app (GiroCode).")}</figcaption>
              </figure>
            )}
            <dl className="portal-transfer">
              <dt>{t("Recipient")}</dt>
              <dd>{pay.name}</dd>
              <dt>IBAN</dt>
              <dd>
                <span className="portal-num">{groupIban(pay.iban)}</span>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  aria-label={t("Copy")}
                  onClick={() => copy("iban", pay.iban)}
                />
                {copied === "iban" && <Text type="success">{t("Copied")}</Text>}
              </dd>
              {pay.bic && (
                <>
                  <dt>BIC</dt>
                  <dd className="portal-num">{pay.bic}</dd>
                </>
              )}
              <dt>{t("Amount")}</dt>
              <dd>
                <strong>{formatEuro(pay.amountCents, lng)}</strong>
              </dd>
              <dt>{t("Reference")}</dt>
              <dd>
                <span>{pay.reference}</span>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  aria-label={t("Copy")}
                  onClick={() => copy("reference", pay.reference)}
                />
                {copied === "reference" && <Text type="success">{t("Copied")}</Text>}
              </dd>
            </dl>
          </div>
          <Paragraph type="secondary" className="portal-gap-top">
            {t("Please always state the reference so that we can assign your payment.")}
          </Paragraph>
        </Card>
      )}
    </div>
  );
};

export default PortalInvoiceDetail;
